import React, { memo } from 'react';
import { useTheme } from 'tamagui';
import ForumIcon from '@mui/icons-material/Forum';
import { CobuText, CobuButton, CobuStack } from '@cobu/ui';

interface EmptyDiscussionStateProps {
  title: string;
  subtitle: string;
  buttonText?: string;
  onButtonPress?: (replyTo?: any) => void;
  showButton?: boolean;
}

/**
 * EmptyDiscussionState Component
 * 
 * Renders an engaging empty state when no comments exist in a discussion.
 * Provides clear visual feedback and encourages user engagement.
 * 
 * Features:
 * - Friendly icon and messaging
 * - Call-to-action button (optional)
 * - Customizable title and subtitle
 * - Responsive design with proper spacing
 * - Accessible design with proper semantic structure
 * 
 * @example
 * ```tsx
 * <EmptyDiscussionState
 *   title="Start the Conversation"
 *   subtitle="Be the first to share your thoughts with the community"
 *   buttonText="Create First Post"
 *   onButtonPress={() => handleCompose()}
 *   showButton={true}
 * />
 * ```
 */
const EmptyDiscussionState: React.FC<EmptyDiscussionStateProps> = memo(({
  title,
  subtitle,
  buttonText,
  onButtonPress,
  showButton = true,
}) => {
  const theme = useTheme();

  return (
    <CobuStack
      f={1}
      ai="center"
      jc="center"
      gap="$4"
      px="$6"
      py="$8"
    >
      {/* Icon */}
      <CobuStack
        w="$8"
        h="$8"
        br="$6"
        bg="$black"
        ai="center"
        jc="center"
        mb="$2"
      >
        <ForumIcon width={32} height={32} style={{ color: theme.white?.val }} />
      </CobuStack>
      
      {/* Title */}
      <CobuText
        variant="body"
        ta="center"
      >
        {title}
      </CobuText>
      
      {/* Subtitle */}
      <CobuText
        variant="body"
        ta="center"
        lh={20}
        maw="80%"
      >
        {subtitle}
      </CobuText>
      
      {/* Action button */}
      {showButton && onButtonPress && buttonText && (
        <CobuButton
          dsVariant="primary"
          title={buttonText}
          onPress={() => onButtonPress()}
          mt="$2"
          px="$6"
        />
      )}
    </CobuStack>
  );
});

EmptyDiscussionState.displayName = 'EmptyDiscussionState';

export default EmptyDiscussionState; 